export const POLLING_SUCCESS = 'POLLING_SUCCESS';
export const POLLING_FAILED = 'POLLING_FAILED';
export const POLLING_PENDING = 'POLLING_PENDING';

/**
 * @template T
 * @param {() => (T|Promise<T>)} task - polling task
 * @param {(pollingTaskResult: T) => "POLLING_SUCCESS"|"POLLING_FAILED"|"POLLING_PENDING"} predication - 決定是否再次執行 polling task
 * @param {object} [options]
 * @param {number} [options.interval=5000] - 再次執行 polling task 的間隔時間 (單位: ms, 預設: 5000ms)
 * @param {AbortSignal} [options.signal] @see {@link https://developer.mozilla.org/en-US/docs/Web/API/AbortController}
 * @return {Promise<T>}
 *
 * @example
 * // 執行 fetchSomething，用 IsSomethingOK 判斷 fetchSomething 的回傳值
 * // IsSomethingOK 如果回傳 POLLING_PENDING，10 秒後會再執行一次 fetchSomething
 * // IsSomethingOK 如果回傳 POLLING_SUCCESS，則回傳 fetchSomething 的回傳值
 * // IsSomethingOK 如果回傳 POLLING_FAILED，則回傳 fetchSomething 的回傳值或者錯誤
 * polling(fetchSomething, IsSomethingOK, { interval: 10000 }).then((something) => { ... });
 *
 * @example
 * // abort polling
 * async function checkStatus() {
 *   // create an abort controller
 *   const abortController = new AbortController();
 *
 *   //
 *   async function task() {
 *     const req = ky.get('/api/endpoint');
 *     abortController.signal.addEventListener('abort', req.abort);
 *     await req;
 *     abortController.signal.removeEventListener('abort', req.abort);
 *     return req;
 *   }
 *
 *   setTimeout(abortController.abort, 15000);
 *
 *   try {
 *     return await polling(task, predication, { signal: abortController.signal });
 *   } catch(error) {
 *     if (error.name 'AbortError') {
 *       // handle abort
 *     }
 *   }
 * }
 */
export default function polling(task, predication, options = {}) {
  const { interval = 5000, signal } = options;

  async function _polling(resolve, reject) {
    function abort() {
      return reject(new DOMException('Aborted', 'AbortError'));
    }

    if (signal instanceof AbortSignal) {
      if (signal.aborted) {
        return abort();
      }

      signal.addEventListener('abort', abort);
    }

    try {
      const result = await task();
      const state = predication(result);

      switch (state) {
        case POLLING_SUCCESS:
          return resolve(result);
        case POLLING_FAILED:
          return reject(result);
        case POLLING_PENDING:
        default:
          return setTimeout(_polling, interval, resolve, reject);
      }
    } catch (error) {
      return reject(error);
    } finally {
      if (signal instanceof AbortSignal) {
        signal.removeEventListener('abort', abort);
      }
    }
  }

  return new Promise(_polling);
}
